import { useState } from 'react';
import { Eye, EyeSlash, Info, LockKey } from '@phosphor-icons/react';

/**
 * One password input, used everywhere a password is typed.
 *
 * The attributes here are not decoration. `type="password"` implies protection
 * from autocapitalise and autocorrect, but revealing the field takes it out of
 * that type and the implicit protection goes with it — so anyone who taps the
 * eye to check their typing and keeps going gets a mangled password unless the
 * attributes are stated explicitly. `dir="ltr"` keeps a Latin password from
 * being laid out right-to-left inside an RTL page, and the monospace stack in
 * `.credential-input` matters for a second reason: the Persian UI font remaps
 * the ASCII digits onto Persian glyphs, so a password with a 3 in it would
 * otherwise render as ۳ and read as a different string than the one typed.
 */
export function PasswordField({
  id, label, value, onValueChange, hint, autoComplete = 'new-password', required = true, disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  hint?: string;
  autoComplete?: 'current-password' | 'new-password';
  required?: boolean;
  disabled?: boolean;
}) {
  const [reveal, setReveal] = useState(false);
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <label className="field smart-field" htmlFor={id}>
      <span className="smart-field__label">{label}</span>
      <span className="smart-field__control smart-field__control--boxed credential-input">
        <LockKey size={18} aria-hidden="true" />
        <input
          id={id}
          aria-label={label}
          type={reveal ? 'text' : 'password'}
          autoComplete={autoComplete}
          dir="ltr"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-describedby={hintId}
          value={value}
          disabled={disabled}
          onChange={(event) => onValueChange(event.target.value)}
          required={required}
        />
        <button
          type="button"
          className="icon-button"
          onClick={() => setReveal(!reveal)}
          aria-label={reveal ? 'پنهان کردن رمز' : 'نمایش رمز'}
        >
          {reveal ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      </span>
      {hint && (
        <small className="smart-field__message" id={hintId}>
          <Info aria-hidden="true" />
          <span>{hint}</span>
        </small>
      )}
    </label>
  );
}
